/**
 * Entrance sound transport.
 *
 * Backend contract (delta + autumn):
 * - upload:  POST {media}/entrance_sounds (multipart `file`) -> { id }
 * - global:  GET  {api}/users/{userId}/entrance_sound -> EntranceSound
 *            PUT  {api}/users/@me/entrance_sound { file_id } -> EntranceSound
 * - server:  PUT  {api}/servers/{serverId}/members/@me/entrance_sound { file_id }
 *            read back from GET {api}/servers/{serverId}/members/{userId}
 *            (`entrance_sound` field). The user GET endpoint does not reflect
 *            member overrides, so it must not be used for server state.
 *
 * Voice ingress plays the member override first, then the global sound.
 */

export interface EntranceSound {
  file_id: string | null;
  url: string | null;
  server_specific: boolean;
}

export interface EntranceSoundTransport {
  apiUrl: string;
  mediaUrl: string;
  /** [header name, header value] */
  authHeader: [string, string];
  fetch?: typeof fetch;
}

/** Scope for an entrance sound: global (undefined) or a server id. */
export type EntranceSoundScope = string | undefined;

/** Backend upload limit for entrance sounds (autumn `entrance_sound_max_size`). */
export const ENTRANCE_SOUND_MAX_BYTES = 5 * 1024 * 1024;

async function request<T>(
  transport: EntranceSoundTransport,
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const [key, value] = transport.authHeader;
  const response = await (transport.fetch ?? fetch)(url, {
    ...init,
    headers: { [key]: value, ...(init.headers ?? {}) },
  });

  const text = await response.text();
  if (!response.ok) {
    let message = text;
    try {
      const parsed = JSON.parse(text) as { type?: string };
      if (parsed?.type) message = parsed.type;
    } catch {
      // not JSON — keep raw text
    }
    throw new Error(
      `Entrance sound request failed (${response.status})${message ? `: ${message}` : ""}`,
    );
  }

  return (text ? JSON.parse(text) : undefined) as T;
}

function soundUrl(transport: EntranceSoundTransport, fileId: string | null) {
  return fileId
    ? `${transport.mediaUrl.replace(/\/$/, "")}/entrance_sounds/${fileId}`
    : null;
}

/**
 * Fetch the entrance sound for a scope.
 * For a server scope this returns only the server-specific override.
 */
export async function fetchEntranceSound(
  transport: EntranceSoundTransport,
  userId: string,
  scope: EntranceSoundScope,
): Promise<EntranceSound> {
  if (scope) {
    const member = await request<{ entrance_sound?: string | null }>(
      transport,
      `${transport.apiUrl}/servers/${scope}/members/${userId}`,
    );
    const fileId = member?.entrance_sound ?? null;
    return {
      file_id: fileId,
      url: soundUrl(transport, fileId),
      server_specific: true,
    };
  }

  const sound = await request<EntranceSound | undefined>(
    transport,
    `${transport.apiUrl}/users/${userId}/entrance_sound`,
  );
  return sound ?? { file_id: null, url: null, server_specific: false };
}

/** Set (or clear with `null`) the entrance sound for a scope. */
export function saveEntranceSound(
  transport: EntranceSoundTransport,
  scope: EntranceSoundScope,
  fileId: string | null,
): Promise<EntranceSound> {
  const url = scope
    ? `${transport.apiUrl}/servers/${scope}/members/@me/entrance_sound`
    : `${transport.apiUrl}/users/@me/entrance_sound`;

  return request<EntranceSound>(transport, url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ file_id: fileId }),
  });
}

/** Validate a picked file before upload; returns an error message or null. */
export function validateEntranceSoundFile(file: {
  size: number;
  type: string;
}): string | null {
  if (file.type && !file.type.startsWith("audio/"))
    return "Choose an audio file.";
  if (file.size > ENTRANCE_SOUND_MAX_BYTES)
    return "Entrance sounds must be 5 MB or smaller.";
  return null;
}

/** Upload an audio file to autumn and return its file id. */
export async function uploadEntranceSound(
  transport: EntranceSoundTransport,
  file: Blob & { name?: string },
): Promise<string> {
  const body = new FormData();
  body.append("file", file, file.name || "entrance-sound");

  const upload = await request<{ id?: string; _id?: string }>(
    transport,
    `${transport.mediaUrl.replace(/\/$/, "")}/entrance_sounds`,
    { method: "POST", body },
  );

  const fileId = upload?.id ?? upload?._id;
  if (!fileId) throw new Error("Upload succeeded but returned no file id");
  return fileId;
}
